import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { cancelSubscription, getActiveTiers, getUser } from "../state/profile_slice";
import { profileActions, menuActions } from "../../../di/di";
import moment from "moment";
import LogoutModalComponent from "./logout_modal_component";
import MessageModalComponent from "./message_modal_component";
import { FiMenu } from "react-icons/fi";
import { AlertTriangle } from "lucide-react";
import ChangePasswordModalComponent from "./change_password_modal_comp";
import "../style/animation.css";
import Toast from "../../../components/popups/toast";
import ModalComponent from "../../../components/popups/modal_component";
import PricingModalComponent from "./pricing_modal_component";
import AddonsModalComponent from "./addons_modal_component";
import ProfileUpdateComp from "../../profileupdate/components/profile_update_comp";
import ProfilePricingComponent from "./profile_pricing_component";
import ProfileHelpComponent from "./profile_help_component";
import ProfileSubsDetailsComponent from "./profile_subs_details_component";
import ReactivateSubscriptionModalComponent from "./reactivate_subscription_modal_component";
import ProfileLoadingComponent from "./profile_loading_component";
import { ArrowLeft } from "lucide-react";
import { ProfileUIAction, sendFeedBack, sendContactUs } from "../state/profile_slice";

export default function ProfileComponent({ showCancelTour }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.profile.user);
  const tiers = useSelector((state) => state.profile.tiers);
  const isToRefreshData = useSelector((state) => state.profile.isToRefreshData);
  const showLogoutModal = useSelector((state) => state.profile.showLogoutModal);
  const showFeedbackModal = useSelector((state) => state.profile.showFeedbackModal);
  const showContactModal = useSelector((state) => state.profile.showContactModal);
  const cancelPlanModalOpen = useSelector((state) => state.profile.cancelPlanModalOpen);
  const isEditMode = useSelector((state) => state.profile.isEditMode);
  const action = useSelector((state) => state.profile.action);
  const message = useSelector((state) => state.profile.message);
  const isContactUsSending = useSelector((state) => state.profile.isContactUsSending);
  const isFeedBackSending = useSelector((state) => state.profile.isFeedBackSending);
  const feedbackText = useSelector((state) => state.profile.feedbackText);
  const feedbackTextError = useSelector((state) => state.profile.feedbackTextError);
  const contactSubject = useSelector((state) => state.profile.contactSubject);
  const contactSubjectError = useSelector((state) => state.profile.contactSubjectError);
  const contactMessage = useSelector((state) => state.profile.contactMessage);
  const contactMessageError = useSelector((state) => state.profile.contactMessageError);
  const promptMessage = useSelector((state) => state.profile.promptMessage);
  const planExpirationDate = useSelector((state) => state.profile.user?.planExpirationDate);
  const cancellingSubscription = useSelector((state) => state.profile.cancellingSubscription);

  useEffect(() => {
    if (isToRefreshData) {
      dispatch(getUser());
      dispatch(profileActions.resetToRefreshDate());
    }
  }, [dispatch, isToRefreshData]);

  useEffect(() => {
    dispatch(getUser());
    dispatch(getActiveTiers());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(profileActions.resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    switch (action) {
      case ProfileUIAction.feedBackSendSuccess:
        Toast.success(message);
        break;
      case ProfileUIAction.feedBackSendError:
        Toast.error(message);
        break;
      case ProfileUIAction.contactUsSendSuccess:
        Toast.success(message);
        break;
      case ProfileUIAction.contactUsSendError:
        Toast.error(message);
        break;
      case ProfileUIAction.showSuccessToast:
        Toast.success(promptMessage);
        break;
      case ProfileUIAction.showErrorToast:
        Toast.error(promptMessage);
        break;
      case ProfileUIAction.showInfoToast:
        Toast.info(promptMessage);
        break;
    }
    dispatch(profileActions.setAction(ProfileUIAction.initial));
  }, [action]);

  const toggleDrawer = () => {
    dispatch(menuActions.toggleMenu());
  };

  const handleContact = () => {
    dispatch(sendContactUs());
  };

  const handleFeedback = () => {
    dispatch(sendFeedBack());
  };

  if (tiers.length === 0 || !user) {
    return <ProfileLoadingComponent />;
  }

  return (
    <div className="h-screen bg-gradient-to-b from-slate-950 via-fuchsia-950 to-gray-950 text-white p-6 relative">
      {/* Menu button - Top Left */}
      <button
        onClick={toggleDrawer}
        className="z-[1] fixed top-4 left-4 w-10 h-10 flex items-center justify-center rounded-full bg-slate-900/50 hover:bg-slate-800/60 text-white transition-colors hidden max-lg:flex"
      >
        <FiMenu className="w-5 h-5" />
      </button>

      <div className="fixed inset-0 flex flex-col items-center lg:ml-[15rem] overflow-y-auto">
        {/* Pricing Section */}
        <ProfilePricingComponent />

        {/* User Profile and Subscription Info */}
        <ProfileSubsDetailsComponent showCancelTour={showCancelTour} />

        {/* Help Section */}
        <ProfileHelpComponent />

        {/* Logout Section */}
        <div className="my-6" style={{ animationDelay: "1s" }}>
          <button
            onClick={() => dispatch(profileActions.setShowLogoutModal(true))}
            className="text-base px-10 py-2.5 bg-transparent border border-red-900/30 text-pink-300 rounded-full hover:bg-red-950/30 hover:text-pink-200 transition-all active:translate-y-0.5 active:shadow-none"
          >
            LOGOUT
          </button>
        </div>
      </div>

      {/* Modals */}
      <LogoutModalComponent isOpen={showLogoutModal} onClose={() => dispatch(profileActions.setShowLogoutModal(false))} />

      <ModalComponent
        isOpen={showContactModal}
        onClose={() => dispatch(profileActions.setShowContactModal(false))}
        title="Contact Us"
        confirmText="Send Message"
        onConfirm={handleContact}
        isLoading={isContactUsSending}
        loadingText="Sending..."
      >
        <div className="mb-4">
          <label className="block text-sm text-pink-200 mb-2">Subject</label>
          <input
            type="text"
            value={contactSubject}
            onChange={(e) => {
              dispatch(profileActions.setContactSubject(e.target.value));
              dispatch(profileActions.setContactSubjectError(""));
            }}
            className="w-full bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg px-4 py-3 text-white placeholder-pink-300/70 transition-all shadow-inner shadow-black/20 focus:border-red-800 outline-none focus:ring-2 focus:ring-red-900/50"
            placeholder="How can we help?"
          />
          {contactSubjectError && <p className="text-red-500 text-xs mt-1">{contactSubjectError}</p>}
        </div>
        <div>
          <label className="block text-sm text-pink-200 mb-2">Message</label>
          <textarea
            value={contactMessage}
            onChange={(e) => {
              dispatch(profileActions.setContactMessage(e.target.value));
              dispatch(profileActions.setContactMessageError(""));
            }}
            rows="4"
            className="w-full bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg px-4 py-3 text-white placeholder-pink-300/70 transition-all shadow-inner shadow-black/20 focus:border-red-800 outline-none focus:ring-2 focus:ring-red-900/50"
            placeholder="Tell us what you need help with..."
          />
          {contactMessageError && <p className="text-red-500 text-xs mt-1">{contactMessageError}</p>}
        </div>
      </ModalComponent>

      <ModalComponent
        isOpen={showFeedbackModal}
        onClose={() => dispatch(profileActions.setShowFeedbackModal(false))}
        title="Submit Feedback"
        confirmText="Send Feedback"
        onConfirm={handleFeedback}
        isLoading={isFeedBackSending}
        loadingText="Sending..."
      >
        <div>
          <label className="block text-sm text-pink-200 mb-2">Your Feedback</label>
          <textarea
            value={feedbackText}
            onChange={(e) => {
              dispatch(profileActions.setFeedbackText(e.target.value));
              dispatch(profileActions.setFeedbackTextError(""));
            }}
            rows="5"
            className="w-full bg-slate-950/80 backdrop-blur-sm border border-red-900/30 rounded-lg px-4 py-3 text-white placeholder-pink-300/70 transition-all shadow-inner shadow-black/20 focus:border-red-800 outline-none focus:ring-2 focus:ring-red-900/50"
            placeholder="Share your experience with Eve or suggestions for improvement..."
          />
          {feedbackTextError && <p className="text-red-500 text-xs mt-1">{feedbackTextError}</p>}
        </div>
      </ModalComponent>

      <PricingModalComponent />
      <MessageModalComponent />
      <ChangePasswordModalComponent />
      <AddonsModalComponent />

      {isEditMode && (
        <div className="fixed lg:ml-[15rem] inset-0 flex items-center justify-center bg-gradient-to-b from-slate-950 via-fuchsia-950 to-gray-950">
          {/* Back button always on the left */}
          <div
            className="absolute top-4 left-4 cursor-pointer flex items-center space-x-2"
            onClick={() => dispatch(profileActions.setEditMode(false))}
          >
            <ArrowLeft size={18} className="mr-1" />
            <span className="text-lg font-small">Back</span>
          </div>

          {/* Profile update component centered */}
          <div className="w-full max-w-2xl px-4 mt-16 md:mt-0">
            <ProfileUpdateComp />
          </div>
        </div>
      )}

      {/* Cancel Plan Modal */}
      {cancelPlanModalOpen && (
        <div className="fixed lg:ml-[15rem] inset-0 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-gradient-to-b from-slate-950/90 to-fuchsia-950/70 backdrop-blur-md border border-red-900/20 rounded-xl p-6 w-full max-w-md mx-4 animate-fade-in-up">
            <h3 className="text-xl font-bold text-white mb-4">Confirm Cancellation</h3>
            <div className="mb-6">
              <p className="text-pink-300 mb-4 text-base">Are you sure you want to cancel your subscription?</p>
              <div className="bg-slate-900/50 backdrop-blur-sm p-4 rounded-lg mb-4 border border-red-900/20">
                <div className="flex items-center">
                  <div className="w-10 h-10 flex-shrink-0 bg-red-900/20 rounded-full flex items-center justify-center mr-3">
                    <AlertTriangle size={20} className="text-pink-400" />
                  </div>
                  <p className="text-sm text-white">
                    You will lose access to the service at the end of your current billing period (
                    {planExpirationDate ? moment(planExpirationDate).format("MMMM D, YYYY") : "N/A"}).
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => dispatch(profileActions.setCancelPlanModalOpen(false))}
                className="text-base px-4 py-2 text-white bg-slate-900/70 rounded-full hover:bg-slate-800/80 border border-red-900/20 transition-all active:translate-y-0.5 active:shadow-none"
              >
                Keep Subscription
              </button>
              <button
                onClick={() => dispatch(cancelSubscription({ userId: user._id }))}
                className="text-base px-4 py-2 text-white bg-red-600 rounded-full hover:bg-red-700 transition-all active:translate-y-0.5 active:shadow-none flex items-center justify-center gap-2"
                disabled={cancellingSubscription}
              >
                {cancellingSubscription ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Cancelling...
                  </>
                ) : (
                  "Cancel Subscription"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Plan Modal */}
      <ReactivateSubscriptionModalComponent />
    </div>
  );
}
